/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ListFilter, ListFilters } from './form-filters-type';
import { isUndefined, omitBy } from 'lodash';
import { WrapperField } from './wrapper-field';
import { useSearchParams } from 'react-router-dom';
import { Expanded } from '../../table/expanded';
import { Button } from 'app/components/atoms/button/button';
import { SlidersHorizontal } from 'lucide-react';

/**
 * `Props` type.
 */

type Props = {
  filters: ListFilters;
  appendFilters: (values: any) => void;
  children: JSX.Element;
  hasNested: boolean;
  defaultQueryString: string | undefined;
};

/**
 * Export `FiltersForm` component.
 */

export function FiltersForm({ filters, hasNested, defaultQueryString, appendFilters, children }: Props): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams(defaultQueryString);
  const [initialValues] = useState<any>(() => {
    if (!searchParams.has('filter')) {
      return {};
    }

    const filter = searchParams.get('filter');
    const page = searchParams.get('page');
    const result = JSON.parse(filter ?? '{}');

    return { ...result, page };
  });

  const [showFilters, setShowFilters] = useState(searchParams.get('filterOpened') === 'true');
  const form = useForm({ mode: 'all', reValidateMode: 'onChange' });
  const { watch } = form;
  const [values, setValues] = useState<any>(initialValues);
  const collapsedFilters = useMemo(() => filters.filter(item => !item.inline), [filters]);
  const inlineFilters = useMemo(() => filters.filter(item => !!item.inline), [filters]);

  // Default filter values (from the list's defaultQueryString) — a filter set to
  // its default (e.g. the default orderBy) does not count as "active".
  const defaultFilterValues = useMemo(() => {
    if (!defaultQueryString) {
      return {} as Record<string, any>;
    }

    try {
      return JSON.parse(new URLSearchParams(defaultQueryString).get('filter') ?? '{}');
    } catch {
      return {} as Record<string, any>;
    }
  }, [defaultQueryString]);

  // When a collapsed filter is active, keep the panel open and lock the toggle.
  const hasActiveCollapsedFilter = useMemo(() => {
    return collapsedFilters.some(item => {
      const name = (item as any).name;
      const value = values?.[name];

      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
        return false;
      }

      return JSON.stringify(value) !== JSON.stringify(defaultFilterValues?.[name]);
    });
  }, [collapsedFilters, values, defaultFilterValues]);

  const filtersOpen = showFilters || hasActiveCollapsedFilter;

  useEffect(() => {
    const subscription = watch(setValues);

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (hasNested || !initialValues) {
      return;
    }

    for (const key of Object.keys(initialValues)) {
      if (initialValues[key] !== undefined && initialValues[key] !== null) {
        form.setValue(key, initialValues[key]);
      }
    }
  }, [form, hasNested, initialValues]);

  useEffect(() => {
    if (hasNested) {
      return;
    }

    const timer = setTimeout(() => {
      clearTimeout(timer);

      const cleanValues = omitBy(values, value => isUndefined(value));

      setSearchParams(searchParams => {
        searchParams.set('filter', JSON.stringify(cleanValues));
        searchParams.set('filterOpened', String(filtersOpen));

        return searchParams;
      });

      appendFilters({ filters: cleanValues });
    }, 250);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendFilters, hasNested, showFilters, values]);

  useEffect(() => {
    if (hasNested) {
      return;
    }

    const data = form.getValues();

    setValues(data);
  }, [form, hasNested]);

  return (
    <>
      <div
        className={
          'grid w-full grid-cols-[1fr_min-content] items-center justify-between gap-4 py-4 max-[600px]:flex max-[600px]:flex-col-reverse'
        }
      >
        <div className={'flex flex-wrap gap-4 max-[600px]:w-full'}>
          {!hasNested &&
            inlineFilters.map((item: ListFilter, index: number) => {
              return <WrapperField form={form} item={item} key={index} values={values} />;
            })}
        </div>

        <div className={'flex items-center gap-4 self-center max-[600px]:w-full max-[600px]:justify-end'}>
          {collapsedFilters.length > 0 && (
            <Button
              disabled={hasActiveCollapsedFilter}
              icon={<SlidersHorizontal className={'h-4 w-4'} />}
              onClick={() => setShowFilters(!showFilters)}
            />
          )}

          {children}
        </div>
      </div>

      <Expanded expanded={filtersOpen}>
        <div className={'mt-4 grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 pb-4'}>
          {!hasNested &&
            collapsedFilters.map((item: ListFilter, index: number) => {
              return <WrapperField form={form} item={item} key={index} values={values} />;
            })}
        </div>
      </Expanded>
    </>
  );
}
